import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { AsmAuthStorageService, TokenTarget } from '@spartacus/asm/root'
import {
  AuthActions,
  AuthStorageService,
  AuthToken,
  ConfigurationService,
  GlobalMessageService,
  GlobalMessageType,
  StateWithClientAuth,
  WindowRef
} from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, Observable } from 'rxjs'
import { take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbUserAccountService } from '../../user/services/eb-user-account.service'
import { EbUserIdService } from './eb-user-id.service'

@Injectable({
  providedIn: 'root'
})
export class EbAuthStatePersistenceService {
  private readonly isValidTokenAvailable$ = new BehaviorSubject(false)

  constructor(
    protected store: Store<StateWithClientAuth>,
    protected userIdService: EbUserIdService,
    protected userAccountService: EbUserAccountService,
    protected authStorageService: AuthStorageService,
    protected asmAuthStorageService: AsmAuthStorageService,
    protected cookieService: CookieService,
    private readonly winRef: WindowRef,
    protected configurationService: ConfigurationService,
    protected globalMessageService: GlobalMessageService,
    protected router: Router
  ) {
    this.syncAccessTokenFromCookie()
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }

  public initializeSync() {
    this.winRef.nativeWindow.addEventListener('storage', evt => {
      // in this case the logout was processed in another browser location (tab)
      if (
        evt?.key === EbLocalStorageKey.AUTH_KEY.toString() &&
        !!JSON.parse(evt.oldValue)?.token?.access_token &&
        !evt?.newValue
      ) {
        this.internalLogout()
      }
    })
  }

  isValidTokenAvailable(): Observable<boolean> {
    if (this.isValidTokenAvailable$.value !== !!this.accessToken) {
      this.isValidTokenAvailable$.next(!!this.accessToken)
    }
    return this.isValidTokenAvailable$
  }

  internalLogout(silent = false, redirectUrl?: string) {
    if (this.winRef.localStorage.getItem('asm_enabled')) {
      this.userIdService.setUserEmulatedState(false)
      this.asmAuthStorageService.setTokenTarget(TokenTarget.User)
      this.asmAuthStorageService.clearEmulatedUserToken()
      this.winRef.localStorage.removeItem('emulatedUserToken')
    }
    this.removeAuthAndToken()
    this.userIdService.setUserId()
    this.userAccountService.setEbActiveUserData(undefined)
    if (!silent && !this.asmSessionEnded()) {
      this.showGlobalSessionExpiredMessage()
    }
    if (redirectUrl) {
      this.router.navigateByUrl(redirectUrl)
    }
  }

  asmSessionEnded(): boolean {
    return this.winRef.localStorage.getItem('asm_enabled') && !this.cookieService.get('asAgent')
  }

  asmCustomerLogout() {
    this.userIdService.setUserEmulatedState(false)
    this.userIdService.setUserId()
    this.userAccountService.setEbActiveUserData(undefined)
    this.isValidTokenAvailable$.next(false)
    this.removeCookieAccessToken()
    this.winRef.localStorage.removeItem('emulatedUserToken')
    this.configurationService.unifiedConfig$.pipe(take(1)).subscribe(config => {
      const { baseSite } = config?.context
      baseSite?.forEach(site => {
        this.winRef.localStorage.removeItem(`spartacus⚿${site}⚿cart`)
      })
    })
    this.router.navigateByUrl('/')
  }

  syncAccessTokenFromCookie() {
    if (this.accessToken) {
      this.authStorageService.setToken(this.generateAuthToken())
      this.winRef.localStorage.setItem(
        EbLocalStorageKey.AUTH_KEY,
        JSON.stringify(this.generateAuthToken())
      )
    }
    this.isValidTokenAvailable$.next(!!this.accessToken)
  }

  private removeAuthAndToken() {
    this.isValidTokenAvailable$.next(false)
    this.store.dispatch(new AuthActions.Logout())
    this.removeCookieAccessToken()
    this.clearLocalStorage()
    this.authStorageService.setItem('access_token', '')
    this.authStorageService.setItem('access_token_stored_at', '')
  }

  private showGlobalSessionExpiredMessage() {
    this.globalMessageService.add(
      {
        key: 'httpHandlers.sessionExpired'
      },
      GlobalMessageType.MSG_TYPE_ERROR
    )
  }

  private removeCookieAccessToken() {
    const hostname = this.winRef.location.hostname
    if (hostname && (hostname.match(/\./g) || []).length > 1) {
      const cookieDomain = hostname.substring(hostname.indexOf('.'))
      this.cookieService.delete('access_token', '/', cookieDomain)
    } else {
      this.cookieService.delete('access_token', '/', hostname)
    }
  }

  private clearLocalStorage() {
    this.configurationService.unifiedConfig$.pipe(take(1)).subscribe(config => {
      const { baseSite } = config?.context
      baseSite?.forEach(site => {
        this.winRef.localStorage.removeItem(`spartacus⚿${site}⚿cart`)
      })
    })
    this.winRef.nativeWindow.localStorage.removeItem(EbLocalStorageKey.AUTH_KEY)
    this.winRef.nativeWindow.dispatchEvent(new Event('storage'))
  }

  private generateAuthToken(): AuthToken {
    return {
      access_token: this.accessToken,
      token_type: 'Bearer',
      granted_scopes: ['basic openid'],
      access_token_stored_at: new Date().getTime().toString()
    }
  }
}
